/**
 * The Billing page's tables: spend by model over the window, this cycle's
 * Stripe lines, the team's rates, and invoice history. Each is a plain
 * hairline table in the design's dialect; the money vocabulary comes from
 * billing.ts, so `not priced` here is the same `not priced` the dashboard
 * uses. Every table renders the rows it is given; the page decides how
 * many that is (ShowMore.tsx).
 */

import type { BillingInvoice, BillingSpendModel, ModelRateView } from '@/api/generated/model'
import {
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_VARIANTS,
  LIST_PRICE_BADGE,
  TOKEN_KIND_LABELS,
  formatAmount,
  formatDay,
  formatPeriod,
  formatQuantity,
  listPriceSourceLabel,
  pricingLabel,
  rateLabel,
  spendLabel,
  unitPriceLabel,
} from '@/components/billing/billing'
import type {
  CycleFlatLine,
  CycleModelGroup,
  CycleRow,
  CycleTokenLine,
} from '@/components/billing/billing'
import { formatCompact } from '@/components/models/home/contracts'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

// Money in ink, supporting counts muted, so a row's amount is what the eye lands on.
const NUM = 'text-right font-mono text-xs tabular-nums text-muted-foreground'
const MONEY = 'text-right font-mono text-xs tabular-nums text-foreground'
const MONO = 'font-mono text-xs'
// A cycle amount's weight follows what it sums: a model's subtotal and the
// amount due are the heaviest figures in the table. Semibold, because the
// mono stack falls back to a system face with no medium weight, where
// font-medium renders as regular.
const TOTAL = `${MONEY} font-semibold`

/** Stripe rounds a line to the cent, so an amount of 0 here is usage under a cent. */
function amountClass(amount: number): string {
  return Math.abs(amount) < 1 ? NUM : MONEY
}

export function SpendByModelTable({ models }: { models: BillingSpendModel[] }): React.JSX.Element {
  if (models.length === 0) {
    return <p className="text-sm text-muted-foreground">No traffic in the last 30 days.</p>
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Model</TableHead>
          <TableHead className="text-right">Requests</TableHead>
          <TableHead className="text-right">Tokens</TableHead>
          <TableHead className="text-right">Spend</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {models.map((row) => (
          <TableRow key={row.model}>
            <TableCell className={MONO}>{row.model === '' ? 'unattributed requests' : row.model}</TableCell>
            <TableCell className={NUM}>{row.request_count.toLocaleString('en-US')}</TableCell>
            <TableCell className={NUM}>{formatCompact(row.total_tokens)}</TableCell>
            <TableCell className={MONEY}>{spendLabel(row.request_count, row.cost_usd)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

interface CycleLinesTableProps {
  /** The cycle's used lines, grouped: `cycleRows(accrual.lines)`. */
  rows: CycleRow[]
  amountDue: number
  currency: string
}

export function CycleLinesTable({ rows, amountDue, currency }: CycleLinesTableProps): React.JSX.Element {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No usage has been metered this cycle yet.</p>
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Line</TableHead>
          <TableHead className="text-right">Quantity</TableHead>
          <TableHead className="text-right">Unit price</TableHead>
          <TableHead className="text-right">Amount</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, index) =>
          row.type === 'model' ? (
            <ModelGroupRows key={`model-${row.model}`} group={row} currency={currency} />
          ) : (
            // The projection carries no line id; the list is read-only and
            // replaced whole on refetch.
            <FlatLineRow key={`line-${String(index)}`} row={row} currency={currency} />
          ),
        )}
        <TableRow>
          <TableCell className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            Amount due
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell className={TOTAL}>{formatAmount(amountDue, currency)}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  )
}

/** A model's subtotal, then its token kinds as compact lines under it. */
function ModelGroupRows({ group, currency }: { group: CycleModelGroup; currency: string }): React.JSX.Element {
  return (
    <>
      <TableRow className="border-0">
        <TableCell className={`${MONO} pb-2`}>{group.model}</TableCell>
        <TableCell className="pb-2" />
        <TableCell className="pb-2" />
        <TableCell className={`${TOTAL} pb-2`}>{formatAmount(group.subtotal, currency)}</TableCell>
      </TableRow>
      {group.lines.map((entry, index) => (
        <TokenLineRow
          key={`${entry.kind}-${String(index)}`}
          entry={entry}
          currency={currency}
          isLast={index === group.lines.length - 1}
        />
      ))}
    </>
  )
}

interface TokenLineRowProps {
  entry: CycleTokenLine
  currency: string
  isLast: boolean
}

function TokenLineRow({ entry, currency, isLast }: TokenLineRowProps): React.JSX.Element {
  const { kind, line } = entry
  // Compact inside the group. The last line carries the group's closing
  // space and the hairline under it.
  const pad = isLast ? 'pb-4 pt-1.5' : 'py-1.5'
  return (
    <TableRow className={cn(!isLast && 'border-0')}>
      <TableCell className={cn('pl-8 text-sm text-muted-foreground', pad)}>{TOKEN_KIND_LABELS[kind]}</TableCell>
      <TableCell className={cn(NUM, pad)}>{formatQuantity(line.quantity)}</TableCell>
      <TableCell className={cn(NUM, pad, 'whitespace-nowrap')}>{unitPriceLabel(line.unit_amount_decimal)}</TableCell>
      <TableCell className={cn(amountClass(line.amount), pad)}>{formatAmount(line.amount, currency)}</TableCell>
    </TableRow>
  )
}

function FlatLineRow({ row, currency }: { row: CycleFlatLine; currency: string }): React.JSX.Element {
  const { label, line } = row
  return (
    <TableRow>
      <TableCell className="whitespace-normal break-words text-sm">{label}</TableCell>
      <TableCell className={NUM}>{formatQuantity(line.quantity)}</TableCell>
      <TableCell className={`${NUM} whitespace-nowrap`}>{unitPriceLabel(line.unit_amount_decimal)}</TableCell>
      <TableCell className={TOTAL}>{formatAmount(line.amount, currency)}</TableCell>
    </TableRow>
  )
}

export function RatesTable({ rates }: { rates: ModelRateView[] }): React.JSX.Element {
  if (rates.length === 0) {
    return <p className="text-sm text-muted-foreground">No models are priced.</p>
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Model</TableHead>
          <TableHead>Rate</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rates.map((rate) => (
          <RateRow key={rate.model} rate={rate} />
        ))}
      </TableBody>
    </Table>
  )
}

function RateRow({ rate }: { rate: ModelRateView }): React.JSX.Element {
  const { model, pricing } = rate
  const source = listPriceSourceLabel(rate)
  return (
    <TableRow>
      <TableCell className={MONO}>{model}</TableCell>
      <TableCell className={MONO}>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span>{pricingLabel(pricing)}</span>
            {rate.source === 'catalog' && <Badge variant="outline">{LIST_PRICE_BADGE}</Badge>}
            {pricing.kind === 'hourly' && <Badge variant="outline">hourly</Badge>}
            {pricing.kind === 'token' && pricing.rates.display_only && (
              <Badge variant="secondary">not invoiced</Badge>
            )}
          </div>
          {source !== null && <div className="text-muted-foreground">{source}</div>}
          {pricing.upstreams.map((override) => (
            <div key={override.upstream_id} className="flex items-center gap-2 text-muted-foreground">
              <span>
                via {override.upstream_id}: {rateLabel(override)}
              </span>
              {override.display_only && <Badge variant="secondary">not invoiced</Badge>}
            </div>
          ))}
        </div>
      </TableCell>
    </TableRow>
  )
}

export function InvoicesTable({ invoices }: { invoices: BillingInvoice[] }): React.JSX.Element {
  if (invoices.length === 0) {
    return <p className="text-sm text-muted-foreground">No invoices have been issued yet.</p>
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Invoice</TableHead>
          <TableHead>Period</TableHead>
          <TableHead>Issued</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Links</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {invoices.map((invoice) => (
          <TableRow key={invoice.id}>
            <TableCell className={MONO}>{invoice.number ?? '—'}</TableCell>
            <TableCell className="text-sm">{formatPeriod(invoice.period_start, invoice.period_end)}</TableCell>
            <TableCell className="text-sm">{formatDay(invoice.created_at)}</TableCell>
            <TableCell className={MONEY}>{formatAmount(invoice.amount_due, invoice.currency)}</TableCell>
            <TableCell>
              <Badge variant={INVOICE_STATUS_VARIANTS[invoice.status]}>
                {INVOICE_STATUS_LABELS[invoice.status]}
              </Badge>
            </TableCell>
            <TableCell className="text-right font-mono text-xs">
              {invoice.hosted_invoice_url !== null && (
                <a href={invoice.hosted_invoice_url} target="_blank" rel="noreferrer" className="underline underline-offset-4">
                  View
                </a>
              )}
              {invoice.hosted_invoice_url !== null && invoice.invoice_pdf !== null && ' · '}
              {invoice.invoice_pdf !== null && (
                <a href={invoice.invoice_pdf} target="_blank" rel="noreferrer" className="underline underline-offset-4">
                  PDF
                </a>
              )}
              {invoice.hosted_invoice_url === null && invoice.invoice_pdf === null && '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
